import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, PenTool, Package } from "lucide-react";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { useSeo } from "@/lib/seo";

const COLLECTIONS = [
  { name: "Custom Apparel", image: "https://static.prod-images.emergentagent.com/jobs/1c646e73-3991-4deb-9e9d-27e0d854e41a/images/376c8cf10f56e0674f8a6a513c0dba1cba20d7f2bab8e14dbc1a1499ae1c6898.jpeg" },
  { name: "Tumblers & Drinkware", image: "https://static.prod-images.emergentagent.com/jobs/1c646e73-3991-4deb-9e9d-27e0d854e41a/images/83edeca6bfc7610fc3c49cdff09c2dde3c5fd6d2e0f0fb3fd26982c9d60cfaf0.jpeg" },
  { name: "Personalized Gifts", image: "https://static.prod-images.emergentagent.com/jobs/1c646e73-3991-4deb-9e9d-27e0d854e41a/images/b15aeff199adbd9b39dca42709dab96418164a09e835c25dd226296754566baa.jpeg" },
  { name: "Home & Living", image: "https://static.prod-images.emergentagent.com/jobs/1c646e73-3991-4deb-9e9d-27e0d854e41a/images/2c2b2c0f3047fcce38e9d0f180fa527cf0499850c4b1110ade275258bb163c38.jpeg" },
  { name: "Resin Art", image: "https://static.prod-images.emergentagent.com/jobs/1c646e73-3991-4deb-9e9d-27e0d854e41a/images/0b8e76ee52e1ec0c2e32a4481ed922a630b150055e8adc2d64c0e4b2ea840f96.jpeg" },
  { name: "Seasonal Collections", image: "https://static.prod-images.emergentagent.com/jobs/1c646e73-3991-4deb-9e9d-27e0d854e41a/images/574b62d6d0d9c3b876cb746b1dc7b37ab564187d0eae22a68481a7f01aded1c8.jpeg" },
];

export default function Home() {
  useSeo(
    "Collins Crafty Creations — Handmade Printing & Custom Apparel | Memphis, TN",
    "Collins Crafty Creations crafts custom apparel, tumblers, personalized gifts, resin art, and made-to-order printing. Shop the collection or start a custom order."
  );
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => {
      const picks = data.filter((p) => p.best_seller || p.featured).slice(0, 4);
      setFeatured((picks.length ? picks : data).slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative bg-navy-dark overflow-hidden grid lg:grid-cols-2 min-h-[88vh]" data-testid="home-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -left-24 w-[440px] h-[440px] rounded-full bg-electric/10 blur-[150px]" />
        </div>
        {/* Left copy */}
        <div className="relative z-10 flex flex-col justify-center px-6 sm:px-10 lg:pl-16 xl:pl-24 pt-28 pb-16 lg:py-0">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-[11px] sm:text-xs tracking-[0.34em] uppercase text-electric font-semibold">Handmade in Memphis, TN</div>
            <h1 className="mt-5 font-serif font-semibold leading-[0.92]">
              <span className="block text-5xl sm:text-6xl xl:text-7xl metal-text">Collins Crafty</span>
              <span className="block text-5xl sm:text-6xl xl:text-7xl metal-text">Creations</span>
            </h1>
            <div className="mt-4 font-script text-4xl sm:text-5xl blue-text drop-shadow-[0_0_16px_rgba(0,140,255,0.35)]">Made to order. Made to mean something.</div>
            <p className="mt-6 text-base sm:text-lg text-chrome/80 leading-relaxed max-w-xl">
              Custom apparel, tumblers, personalized gifts, resin art & bespoke printing —
              handcrafted for every occasion, person, and purpose.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/shop" data-testid="hero-shop"
                className="group inline-flex items-center justify-center gap-2 rounded-lg bg-royal text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] hover:bg-electric transition-colors glow-blue">
                Shop the Collection <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/shop/studio" data-testid="hero-studio"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-chrome/30 bg-white/5 backdrop-blur-sm text-chrome-bright px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] hover:bg-white/10 hover:border-electric transition-colors">
                Start a Custom Order
              </Link>
            </div>
          </motion.div>
        </div>
        {/* Right image */}
        <div className="relative hidden lg:block p-8 xl:p-12">
          <div className="relative w-full h-full rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_30px_90px_rgba(0,0,0,0.55)]">
            <img src={ASSETS.craftyHero} alt="Collins Crafty Creations custom apparel" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/70 via-transparent to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-electric/10" style={{ boxShadow: "0 0 60px rgba(0,140,255,0.15) inset" }} />
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="bg-navy-dark py-24" data-testid="home-collections">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-xs tracking-[0.28em] uppercase text-electric font-semibold">Shop by Collection</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mt-2">Find your favorite craft.</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-chrome hover:text-electric transition-colors" data-testid="collections-all">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {COLLECTIONS.map((c, i) => (
              <Link key={c.name} to="/shop" data-testid={`collection-${i}`}
                className={`group relative overflow-hidden rounded-2xl ring-1 ring-white/10 ${i === 0 ? "col-span-2 lg:col-span-2 aspect-[16/10]" : "aspect-square"}`}>
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/85 via-navy-dark/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between">
                  <h3 className="font-serif text-xl sm:text-2xl text-white group-hover:text-electric transition-colors">{c.name}</h3>
                  <span className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white group-hover:border-electric group-hover:text-electric group-hover:bg-royal/25 transition-all"><ArrowUpRight size={16} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOM STUDIO BAND */}
      <section className="relative overflow-hidden bg-midnight py-24" data-testid="home-studio">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-dark to-midnight" />
        <div className="absolute -top-24 right-1/4 w-[420px] h-[420px] rounded-full bg-electric/10 blur-[150px]" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)] order-2 lg:order-1">
            <img src={COLLECTIONS[0].image} alt="Custom order studio" className="w-full h-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-xs tracking-[0.28em] uppercase text-electric font-semibold flex items-center gap-2"><PenTool size={14} /> Custom Order Studio</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-white mt-4 leading-tight">Dream it. We'll craft it.</h2>
            <p className="text-chrome/70 mt-5 leading-relaxed max-w-lg">
              Have a vision? Upload your artwork or let us design it. Our custom order studio walks you through every
              detail — product, sizes, placement, and files — then we send a secure quote to make it real.
            </p>
            <div className="mt-7 flex flex-wrap gap-5">
              {[{ Icon: Sparkles, t: "Bring your design, or we create it" }, { Icon: Package, t: "Apparel, drinkware, gifts & more" }].map((f) => (
                <div key={f.t} className="flex items-center gap-2 text-sm text-chrome/70"><f.Icon size={16} className="text-electric" /> {f.t}</div>
              ))}
            </div>
            <Link to="/shop/studio" data-testid="studio-cta"
              className="group mt-9 inline-flex items-center gap-2 rounded-lg bg-royal text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] hover:bg-electric transition-colors glow-blue">
              Start Your Custom Order <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="bg-navy-dark py-24" data-testid="home-featured">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <div className="text-xs tracking-[0.28em] uppercase text-electric font-semibold">Best Sellers</div>
                <h2 className="font-serif text-3xl sm:text-4xl text-white mt-2">Crowd favorites.</h2>
              </div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-chrome hover:text-electric transition-colors" data-testid="featured-all">
                Shop all <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {featured.map((p) => (
                <Link key={p.id} to="/shop" data-testid={`featured-${p.id}`}
                  className="group rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-electric/40 transition-all">
                  <div className="aspect-square overflow-hidden">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <div className="text-[11px] uppercase tracking-wide text-chrome/45">{p.category}</div>
                    <div className="font-serif text-lg text-white mt-1 leading-tight group-hover:text-electric transition-colors line-clamp-1">{p.name}</div>
                    <div className="text-electric font-semibold mt-2">${Number(p.price).toFixed(2)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE / CTA BAND */}
      <section className="relative overflow-hidden bg-black" data-testid="home-quote">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${ASSETS.quoteBridge})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/95 to-navy-dark/70" aria-hidden="true" />
        <div className="absolute -bottom-24 left-1/3 w-[440px] h-[440px] rounded-full bg-electric/15 blur-[140px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="text-center lg:text-left max-w-2xl">
            <div className="font-script text-4xl sm:text-5xl blue-text leading-snug drop-shadow-[0_0_18px_rgba(0,140,255,0.3)]">Handmade with heart.<br className="hidden sm:block" /> Crafted just for you.</div>
          </div>
          <Link to="/contact" data-testid="quote-get-in-touch"
            className="group inline-flex items-center gap-2 rounded-lg bg-royal text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-electric transition-colors glow-blue shrink-0">
            Get In Touch <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </>
  );
}
